import React, { useState } from 'react';
import { InfoIcon, AlertTriangleIcon, AwardIcon, UsersIcon, SettingsIcon, DollarSignIcon } from '../lib/icons';

export default function ReadmeContent() {
    const [activeSection, setActiveSection] = useState('about');

    return (
        <div className="max-w-7xl mx-auto">
            {/* Navigation Tabs */}
            <div className="flex justify-center mb-6">
                <div className="flex space-x-1 bg-gray-800/80 p-1 rounded-lg border border-gray-700">
                    <button 
                        onClick={() => setActiveSection('about')} 
                        className={`px-4 py-2 text-sm font-medium rounded-md transition flex items-center ${activeSection === 'about' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                    >
                        <InfoIcon className="h-4 w-4 mr-2"/>About
                    </button>
                    <button 
                        onClick={() => setActiveSection('bidder')} 
                        className={`px-4 py-2 text-sm font-medium rounded-md transition flex items-center ${activeSection === 'bidder' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                    >
                        <SettingsIcon className="h-4 w-4 mr-2"/>Bidder
                    </button>
                    <button 
                        onClick={() => setActiveSection('auctioneer')} 
                        className={`px-4 py-2 text-sm font-medium rounded-md transition flex items-center ${activeSection === 'auctioneer' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                    >
                        <UsersIcon className="h-4 w-4 mr-2"/>Auctioneer
                    </button>
                </div>
            </div>

            {/* About Section */}
            {activeSection === 'about' && (
                <div>
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h1 className="text-3xl font-bold text-blue-400 mb-4 flex items-center">
                            <InfoIcon className="h-6 w-6 mr-2"/> About This Platform
                        </h1>
                
                {/* Main Model Overview */}
                <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-6 mb-6">
                    <div className="text-center mb-4">
                        <div className="flex justify-center space-x-4 mb-4">
                            <div className="text-4xl">⚡</div>
                            <div className="text-4xl">🔋</div>
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-4">Advanced Auction Theory Platform</h2>
                        <div className="flex justify-center mb-4">
                            <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                🎉 Version 1.0 Release
                            </span>
                        </div>
                        <p className="text-gray-300 text-lg">
                            A comprehensive dual-perspective platform for renewable energy procurement auctions featuring both bidder and auctioneer 
                            dashboards with advanced auction theory, strategic analysis, Monte Carlo simulation, and Winner's Curse correction.
                        </p>
                    </div>
                    <div className="flex justify-center space-x-8 text-blue-400 text-sm">
                        <div className="flex items-center">
                            <span className="mr-2">👤</span>
                            <span>Created by Fernando Izeta</span>
                        </div>
                        <div className="flex items-center">
                            <span className="mr-2">📅</span>
                            <span>August 2025</span>
                        </div>
                        <div className="flex items-center">
                            <span className="mr-2">🚀</span>
                            <span>v1.0 Production Ready</span>
                        </div>
                    </div>
                </div>

                {/* Three-Column Feature Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
                        <div className="text-center mb-4">
                            <div className="text-3xl mb-2">🎯</div>
                            <h3 className="text-xl font-semibold text-white mb-2">Purpose</h3>
                        </div>
                        <p className="text-gray-300 text-sm">
                            Provide comprehensive auction theory analysis and strategic bidding tools for renewable energy procurement auctions
                        </p>
                    </div>
                    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
                        <div className="text-center mb-4">
                            <div className="text-3xl mb-2">⚡</div>
                            <h3 className="text-xl font-semibold text-white mb-2">Technology</h3>
                        </div>
                        <p className="text-gray-300 text-sm">
                            Built with React, featuring interactive simulations, real-time calculations, and comprehensive auction theory education
                        </p>
                    </div>
                    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
                        <div className="text-center mb-4">
                            <div className="text-3xl mb-2">📊</div>
                            <h3 className="text-xl font-semibold text-white mb-2">Features</h3>
                        </div>
                        <p className="text-gray-300 text-sm">
                            4 interactive tabs: Bidder Dashboard, Auctioneer Dashboard, Theory, and comprehensive documentation with dual-perspective analysis
                        </p>
                    </div>
                </div>

                {/* Model Highlights */}
                <div className="mb-6">
                    <h3 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                        <span className="mr-2">⭐</span> Model Highlights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                            <div className="text-center mb-3">
                                <div className="text-2xl mb-2">💰</div>
                                <h4 className="text-lg font-semibold text-white mb-2">Auction Analysis</h4>
                            </div>
                            <ul className="text-gray-300 text-sm space-y-1">
                                <li>• Complete auction theory framework</li>
                                <li>• Revenue equivalence theorem analysis</li>
                                <li>• Winner's Curse correction formulas</li>
                                <li>• Strategic bidding recommendations</li>
                                <li>• Multi-attribute auction modeling</li>
                                <li>• Procurement auction optimization</li>
                            </ul>
                        </div>
                        <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                            <div className="text-center mb-3">
                                <div className="text-2xl mb-2">🧮</div>
                                <h4 className="text-lg font-semibold text-white mb-2">Simulation Engine</h4>
                            </div>
                            <ul className="text-gray-300 text-sm space-y-1">
                                <li>• Interactive bidding simulation</li>
                                <li>• Competitor analysis modeling</li>
                                <li>• Risk-adjusted bid optimization</li>
                                <li>• Multi-scenario analysis</li>
                                <li>• Real-time profit calculations</li>
                                <li>• Strategic decision framework</li>
                            </ul>
                        </div>
                        <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                            <div className="text-center mb-3">
                                <div className="text-2xl mb-2">📚</div>
                                <h4 className="text-lg font-semibold text-white mb-2">Theory Education</h4>
                            </div>
                            <ul className="text-gray-300 text-sm space-y-1">
                                <li>• Comprehensive auction theory</li>
                                <li>• 7 detailed theory chapters</li>
                                <li>• Interactive visualizations</li>
                                <li>• Mathematical foundations</li>
                                <li>• Practical applications</li>
                                <li>• Strategic insights</li>
                            </ul>
                        </div>
                        <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                            <div className="text-center mb-3">
                                <div className="text-2xl mb-2">🎯</div>
                                <h4 className="text-lg font-semibold text-white mb-2">Decision Support</h4>
                            </div>
                            <ul className="text-gray-300 text-sm space-y-1">
                                <li>• Strategic bidding guidance</li>
                                <li>• Winner's Curse prevention</li>
                                <li>• Bidder pool optimization</li>
                                <li>• Risk assessment tools</li>
                                <li>• Procurement recommendations</li>
                                <li>• Cost optimization strategies</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Enterprise-Grade Analytics Engine */}
                <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-6">
                    <h4 className="text-xl font-semibold text-purple-400 mb-3 flex items-center">
                        <span className="mr-2">🧠</span> Enterprise-Grade Auction Theory Platform
                    </h4>
                    <p className="text-gray-300">
                        This platform introduces a revolutionary auction theory application that transforms basic bidding strategies into sophisticated 
                        procurement decision support. The system now rivals commercial auction theory software with advanced risk analysis, 
                        strategic optimization, and comprehensive scenario planning capabilities for renewable energy projects.
                    </p>
                </div>

                {/* References Section */}
                <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                    <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                        <span className="mr-2">📚</span> References & Academic Foundation
                    </h2>
                    <p className="text-gray-300 mb-4">
                        This platform is built upon rigorous academic research in auction theory. The implementation follows 
                        the mathematical foundations and strategic principles established by leading economists and researchers.
                    </p>
                    
                    <div className="bg-gray-900/50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-white mb-3">Key Academic References</h3>
                        <div className="text-sm text-gray-300 space-y-2 max-h-64 overflow-y-auto">
                            <div className="grid grid-cols-1 gap-2">
                                <p>• <strong>Bajari, Patrick (1997)</strong> The First Price Auction with Asymmetric Bidders: Theory and Applications, University of Minnesota, PhD Dissertation</p>
                                <p>• <strong>Bazerman M. H. and William Samualson (1983)</strong> I Won the Auction but Don't Want the Prize, Journal of Conflict Resolution 27 618-634</p>
                                <p>• <strong>Bikhchnadari Sushil (1988)</strong> Reputation in Repeated Auctions, Journal of Economic Theory, 46 97-119</p>
                                <p>• <strong>Bulow, Jeremy and Paul Klemperer (2002)</strong> Prices and the Winner's Curse, Rand Journal of Economics</p>
                                <p>• <strong>Bulow, Jeremy and J. Roberts (1989)</strong> The Simple Economics of Optimal Auctions, Journal of Political Economy, 97 1060-1090</p>
                                <p>• <strong>Campbell, Colin and Dan Levine (2003)</strong> Why and When Not to Auction</p>
                                <p>• <strong>Cassady, R (1967)</strong> Auctions and Auctioneering, University of California Press, Berkeley, California</p>
                                <p>• <strong>Kagel, John and Dan Levin (1986)</strong> The Winner's Curse and Public Information in Common Value Auctions, American Economic Review, 76, 894-920</p>
                                <p>• <strong>Kagel, John and Dan Levin (2002)</strong> Common value Auctions and the Winner's Curse, Princeton University Press</p>
                                <p>• <strong>Kagel, John (1995)</strong> Auctions: A Survey of Experimental Research, in J. Kagel and A. Roth (eds.) Handbook of Experimental Economics, Princeton University Press, 501-585</p>
                                <p>• <strong>Klemperer, Paul (1998)</strong> Auctions with Almost Common Values, European Economic review, 42 757-769</p>
                                <p>• <strong>Klemperer, Paul (2000)</strong> Auction Theory: A Guide to the Literature</p>
                                <p>• <strong>Klemperer, Paul (2002a)</strong> What really Matters in Auction Design, Journal of Economic Perspectives 16(10) 169-189</p>
                                <p>• <strong>Klemperer, Paul (2002b)</strong> How (Not) to Run Auctions: the European 3G Telecom Auctions, European Economic Review 46(4-5) 829-845</p>
                                <p>• <strong>Krishna, Vijay (2002)</strong> Auction Theory, Academic Press</p>
                                <p>• <strong>Laffont, Jean-Jacques (1997)</strong> Game Theory and Empirical Economics: The Case of Auction data, European Economic Review 41 1-35</p>
                                <p>• <strong>Levin, Dan, John Kagel and Jean-Francois Richard (1996)</strong> Revenue Effects and Information Processing in English Common value Auctions, American Economic Review, 86 (3) 442-460</p>
                                <p>• <strong>Malvey, Paul and Christine Archibald (1998)</strong> Uniform Price Auctions, Update of the Treasury Experience, US Treasury</p>
                                <p>• <strong>McMillan, John (2002)</strong> Reinventing the Bazaar: The Natural History of Markets, W. W. Norton & Co.</p>
                                <p>• <strong>Milgrom, Paul and Robert Weber (1982)</strong> A Theory of Auctions and Competitive Bidding, Econometrica 50 1089-1122</p>
                                <p>• <strong>Myerson, Roger (1981)</strong> Optimal Auction Design, Mathematics of Operation Research, 6, 58-73</p>
                                <p>• <strong>Riley, John and William Samuelson (1981)</strong> Optimal Auctions, American Economic Review, 71, 141-145</p>
                                <p>• <strong>Vickrey, William (1961)</strong> Counter Speculations, Auctions and Competitive Bid Tenders, Journal of Finance 16, 8-37</p>
                                <p>• <strong>Wilson, Robert (1977)</strong> A Bidding Model of Perfect Competition, Review of Economic Studies, 44 (3) 511-518</p>
                                <p>• <strong>Wolfstetter, Elmar (1999)</strong> Topics in Microeconomics: Industrial Organization, Auctions and Incentives, Cambridge University Press</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mt-4">
                        <h4 className="text-lg font-semibold text-blue-400 mb-2">Academic Rigor</h4>
                        <p className="text-gray-300 text-sm">
                            All mathematical formulations, bidding strategies, and auction mechanisms implemented in this platform 
                            are based on peer-reviewed research and established economic theory. The Winner's Curse correction, 
                            revenue equivalence principles, and optimal bidding strategies follow the foundational work of Nobel 
                            Prize winners and leading auction theorists.
                        </p>
                    </div>
                </div>
            </div>

            {/* The Model Section */}
            <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                <h1 className="text-3xl font-bold text-green-400 mb-4 flex items-center">
                    <span className="mr-2">🔬</span> The Model
                </h1>
                <p className="text-gray-300 mb-4">
                    This section explains the auction theory applied to your specific renewable energy procurement scenario, 
                    detailing how to achieve the least cost and the impact of reducing the pool of auctioneers.
                </p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                <h1 className="text-3xl font-bold text-blue-400 mb-4">💡 Your Bidding Challenge</h1>
                <p className="text-gray-300 mb-4">
                    You're participating in a <strong>reverse auction</strong> for renewable energy projects. Unlike traditional auctions where the highest bidder wins, 
                    in reverse auctions, the <strong>lowest bidder wins</strong>. This creates unique strategic considerations for both buyers and suppliers.
                </p>
                <p className="text-gray-300 mb-4">
                    Your goal is to achieve the <strong>least cost</strong> for your renewable energy project while ensuring quality and reliability. 
                    Understanding auction theory helps you make strategic decisions about bidder pool size and auction format.
                </p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold text-green-400 mb-4 flex items-center">
                    <AlertTriangleIcon className="h-6 w-6 mr-2"/> The Winner's Curse in Your Context
                </h2>
                <p className="text-gray-300 mb-4">
                    Renewable energy projects are particularly susceptible to the <strong>Winner's Curse</strong> because:
                </p>
                <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
                    <li>Project costs have significant uncertainty (weather, material prices, regulatory changes)</li>
                    <li>All bidders face similar cost structures but with different efficiencies</li>
                    <li>The lowest bidder may have underestimated true project costs</li>
                </ul>
                
                <div className="bg-gray-900/50 p-4 rounded-lg mb-4">
                    <h3 className="text-lg font-semibold text-white mb-2">Cost Correction Formula</h3>
                    <p className="text-gray-300 mb-2">
                        To avoid the Winner's Curse, adjust your cost estimate:
                    </p>
                    <div className="bg-gray-800 p-3 rounded text-center">
                        <p className="text-lg text-white font-mono">
                            Adjusted Cost = Original Estimate + (n-1)/(n+1) × Uncertainty Range
                        </p>
                    </div>
                    <p className="text-gray-300 mt-2 text-sm">
                        Where <em>n</em> is the number of bidders and <em>Uncertainty Range</em> is your estimate of cost variation.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-900/50 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold text-blue-400 mb-2">Example: 5 Bidders</h4>
                        <p className="text-gray-300 text-sm">
                            Original estimate: $1,000,000<br/>
                            Uncertainty: ±$200,000<br/>
                            Correction: +$133,333<br/>
                            <strong>Adjusted bid: $1,133,333</strong>
                        </p>
                    </div>
                    <div className="bg-gray-900/50 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold text-blue-400 mb-2">Example: 10 Bidders</h4>
                        <p className="text-gray-300 text-sm">
                            Original estimate: $1,000,000<br/>
                            Uncertainty: ±$200,000<br/>
                            Correction: +$163,636<br/>
                            <strong>Adjusted bid: $1,163,636</strong>
                        </p>
                    </div>
                </div>
                
                <p className="text-gray-300 mb-4">
                    <strong>Key Insight:</strong> More bidders mean larger corrections needed. This is because winning against more competitors 
                    suggests you may have been overly optimistic about costs.
                </p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center">
                    <UsersIcon className="h-6 w-6 mr-2"/> Impact of Reducing Bidders
                </h2>
                <p className="text-gray-300 mb-4">
                    Reducing the pool of auctioneers has significant implications for your project costs:
                </p>
                
                <div className="bg-gray-900/50 p-4 rounded-lg mb-4">
                    <h3 className="text-lg font-semibold text-white mb-2">Price Impact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                        <div>
                            <div className="text-2xl font-bold text-red-400">+15-25%</div>
                            <div className="text-sm text-gray-400">2-3 bidders</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-yellow-400">+8-15%</div>
                            <div className="text-sm text-gray-400">4-6 bidders</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-green-400">+3-8%</div>
                            <div className="text-sm text-gray-400">7+ bidders</div>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-900/50 p-4 rounded-lg mb-4">
                    <svg width="100%" height="200" viewBox="0 0 400 200" className="mx-auto">
                        <title>Bidder Count vs. Winner's Curse Correction</title>
                        <line x1="50" y1="150" x2="350" y2="150" stroke="#8b949e" strokeWidth="1"/>
                        <line x1="50" y1="50" x2="50" y2="150" stroke="#8b949e" strokeWidth="1"/>
                        <text x="200" y="170" font-size="12" fill="#8b949e" text-anchor="middle">Number of Bidders</text>
                        <text x="25" y="100" font-size="12" fill="#8b949e" transform="rotate(-90, 25, 100)" text-anchor="middle">Correction Factor</text>
                        
                        {/* Correction curve */}
                        <path d="M 50 140 Q 100 120, 150 100 Q 200 80, 250 60 Q 300 40, 350 30" stroke="#58a6ff" strokeWidth="2" fill="none"/>
                        
                        {/* Data points */}
                        <circle cx="50" cy="140" r="3" fill="#58a6ff"/>
                        <circle cx="100" cy="120" r="3" fill="#58a6ff"/>
                        <circle cx="150" cy="100" r="3" fill="#58a6ff"/>
                        <circle cx="200" cy="80" r="3" fill="#58a6ff"/>
                        <circle cx="250" cy="60" r="3" fill="#58a6ff"/>
                        <circle cx="300" cy="40" r="3" fill="#58a6ff"/>
                        <circle cx="350" cy="30" r="3" fill="#58a6ff"/>
                        
                        {/* Labels */}
                        <text x="50" y="160" font-size="10" fill="#8b949e" text-anchor="middle">2</text>
                        <text x="100" y="160" font-size="10" fill="#8b949e" text-anchor="middle">4</text>
                        <text x="150" y="160" font-size="10" fill="#8b949e" text-anchor="middle">6</text>
                        <text x="200" y="160" font-size="10" fill="#8b949e" text-anchor="middle">8</text>
                        <text x="250" y="160" font-size="10" fill="#8b949e" text-anchor="middle">10</text>
                        <text x="300" y="160" font-size="10" fill="#8b949e" text-anchor="middle">12</text>
                        <text x="350" y="160" font-size="10" fill="#8b949e" text-anchor="middle">15</text>
                    </svg>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-red-900/20 border border-red-700/50 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold text-red-400 mb-2">Few Bidders (2-3)</h4>
                        <ul className="text-gray-300 text-sm space-y-1">
                            <li>• Higher prices due to limited competition</li>
                            <li>• Lower Winner's Curse correction needed</li>
                            <li>• Risk of collusion or market power</li>
                            <li>• Reduced innovation and quality options</li>
                        </ul>
                    </div>
                    <div className="bg-green-900/20 border border-green-700/50 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold text-green-400 mb-2">Many Bidders (7+)</h4>
                        <ul className="text-gray-300 text-sm space-y-1">
                            <li>• Lower prices through competition</li>
                            <li>• Higher Winner's Curse correction needed</li>
                            <li>• More innovation and quality options</li>
                            <li>• Better risk distribution</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold text-purple-400 mb-4 flex items-center">
                    <AwardIcon className="h-6 w-6 mr-2"/> Strategic Recommendations
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-xl font-semibold text-blue-400 mb-3">For Buyers (Auctioneers)</h3>
                        <div className="space-y-3">
                            <div className="bg-gray-900/50 p-3 rounded-lg">
                                <h4 className="font-semibold text-white mb-1">Maximize Participation</h4>
                                <p className="text-gray-300 text-sm">
                                    Encourage more bidders through clear requirements, reasonable deadlines, and transparent evaluation criteria.
                                </p>
                            </div>
                            <div className="bg-gray-900/50 p-3 rounded-lg">
                                <h4 className="font-semibold text-white mb-1">Multi-Attribute Scoring</h4>
                                <p className="text-gray-300 text-sm">
                                    Use scoring systems that consider quality, experience, and price to avoid race-to-the-bottom pricing.
                                </p>
                            </div>
                            <div className="bg-gray-900/50 p-3 rounded-lg">
                                <h4 className="font-semibold text-white mb-1">Sealed Bid Format</h4>
                                <p className="text-gray-300 text-sm">
                                    Use sealed bids to prevent collusion and give all bidders equal opportunity regardless of size.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div>
                        <h3 className="text-xl font-semibold text-green-400 mb-3">For Bidders (Suppliers)</h3>
                        <div className="space-y-3">
                            <div className="bg-gray-900/50 p-3 rounded-lg">
                                <h4 className="font-semibold text-white mb-1">Winner's Curse Correction</h4>
                                <p className="text-gray-300 text-sm">
                                    Always adjust your bid upward based on the number of competitors to avoid winning unprofitable contracts.
                                </p>
                            </div>
                            <div className="bg-gray-900/50 p-3 rounded-lg">
                                <h4 className="font-semibold text-white mb-1">Competitor Analysis</h4>
                                <p className="text-gray-300 text-sm">
                                    Research likely competitors and their cost structures to estimate your relative position in the auction.
                                </p>
                            </div>
                            <div className="bg-gray-900/50 p-3 rounded-lg">
                                <h4 className="font-semibold text-white mb-1">Quality Differentiation</h4>
                                <p className="text-gray-300 text-sm">
                                    Emphasize unique capabilities and quality advantages rather than competing solely on price.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                    <InfoIcon className="h-6 w-6 mr-2"/> Using This Tool Effectively
                </h2>
                <p className="text-gray-300 mb-4">
                    The simulation tool helps you understand how different scenarios affect your bidding strategy:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-900/50 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold text-white mb-2">Model Your Competitors</h4>
                        <ul className="text-gray-300 text-sm space-y-1">
                            <li>• Estimate their cost structures</li>
                            <li>• Consider their risk tolerance</li>
                            <li>• Account for their experience levels</li>
                            <li>• Factor in their capacity constraints</li>
                        </ul>
                    </div>
                    <div className="bg-gray-900/50 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold text-white mb-2">Optimize Your Strategy</h4>
                        <ul className="text-gray-300 text-sm space-y-1">
                            <li>• Test different bid levels</li>
                            <li>• Balance win probability vs. profit</li>
                            <li>• Consider multi-attribute scoring</li>
                            <li>• Account for Winner's Curse</li>
                        </ul>
                    </div>
                </div>
                
                <div className="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg">
                    <h4 className="text-lg font-semibold text-blue-400 mb-2">Key Success Factors</h4>
                    <p className="text-gray-300 text-sm">
                        <strong>For Buyers:</strong> Focus on attracting quality bidders through clear specifications, reasonable timelines, 
                        and transparent evaluation. More bidders generally mean better prices and more options.
                    </p>
                    <p className="text-gray-300 text-sm mt-2">
                        <strong>For Bidders:</strong> Understand your true costs, research competitors, and always correct for the Winner's Curse. 
                        Don't win contracts you can't profitably deliver.
                    </p>
                    </div>
                </div>
            </div>
            )}

            {/* Bidder Section */}
            {activeSection === 'bidder' && (
                <div>
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h1 className="text-3xl font-bold text-green-400 mb-4 flex items-center">
                            <SettingsIcon className="h-6 w-6 mr-2"/> Bidder Dashboard
                        </h1>
                        <p className="text-gray-300 mb-4">
                            The Bidder Dashboard is designed for suppliers participating in renewable energy procurement auctions. 
                            It provides comprehensive tools to optimize your bidding strategy and avoid the Winner's Curse.
                        </p>
                    </div>

                    {/* Key Features */}
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h2 className="text-2xl font-bold text-blue-400 mb-4">Key Features</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                                    <DollarSignIcon className="h-5 w-5 mr-2"/> Cost Analysis
                                </h3>
                                <ul className="text-gray-300 space-y-2">
                                    <li>• <strong>Advanced Cost Breakdown:</strong> Model detailed project costs by component (panels, inverters, structures, etc.)</li>
                                    <li>• <strong>Project Types:</strong> Pre-configured cost structures for PV Solar, Solar+BESS, Wind projects</li>
                                    <li>• <strong>Winner's Curse Correction:</strong> Automatic upward adjustment using formula: s + ((n-1)/(n+1)) × ε</li>
                                    <li>• <strong>Multi-Currency Support:</strong> USD, EUR, CNY, PHP with proper formatting</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                                    <UsersIcon className="h-5 w-5 mr-2"/> Competitor Modeling
                                </h3>
                                <ul className="text-gray-300 space-y-2">
                                    <li>• <strong>Competitor Profiles:</strong> Model Aggressive, Established, and New Entrant bidder types</li>
                                    <li>• <strong>Cost Structures:</strong> Define competitor cost means and standard deviations</li>
                                    <li>• <strong>Behavioral Types:</strong> Different markup strategies based on bidder characteristics</li>
                                    <li>• <strong>Dynamic Scaling:</strong> Adjust competitor count to model various market scenarios</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Auction Strategy Guide */}
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h2 className="text-2xl font-bold text-purple-400 mb-4">Auction Strategy Guide</h2>
                        <p className="text-gray-300 mb-4">
                            The dashboard provides dynamic strategy guidance that adapts based on the selected auction type:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gray-900/50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-green-400 mb-2">First-Price & Dutch Auctions</h4>
                                <ul className="text-gray-300 text-sm space-y-1">
                                    <li>• Strategic bidding above corrected cost</li>
                                    <li>• Competition-based markup optimization</li>
                                    <li>• Monte Carlo simulation for optimal bid</li>
                                    <li>• Risk-adjusted profit maximization</li>
                                </ul>
                            </div>
                            <div className="bg-gray-900/50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-blue-400 mb-2">English & Vickrey Auctions</h4>
                                <ul className="text-gray-300 text-sm space-y-1">
                                    <li>• Truthful bidding at corrected cost</li>
                                    <li>• Dominant strategy implementation</li>
                                    <li>• Second-price payment protection</li>
                                    <li>• Incentive-compatible mechanisms</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Simulation Engine */}
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h2 className="text-2xl font-bold text-yellow-400 mb-4">Advanced Simulation Engine</h2>
                        <div className="bg-gray-900/50 p-4 rounded-lg mb-4">
                            <h3 className="text-lg font-semibold text-white mb-2">Monte Carlo Analysis</h3>
                            <p className="text-gray-300 text-sm mb-3">
                                Runs thousands of virtual auctions to analyze the trade-off between bid price, win probability, and expected profit.
                            </p>
                            <ul className="text-gray-300 text-sm space-y-1">
                                <li>• <strong>Performance Scaling:</strong> 1,000-5,000 simulations based on bidder count</li>
                                <li>• <strong>Multi-Attribute Scoring:</strong> Considers price, technical score, and experience</li>
                                <li>• <strong>Real-time Visualization:</strong> Interactive charts showing optimal bidding strategies</li>
                                <li>• <strong>Strategic Recommendations:</strong> Clear guidance on recommended bid prices</li>
                            </ul>
                        </div>
                        <div className="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg">
                            <h4 className="text-lg font-semibold text-blue-400 mb-2">Theory-Compliant Calculations</h4>
                            <p className="text-gray-300 text-sm">
                                All simulations strictly follow procurement auction theory with proper Winner's Curse corrections, 
                                revenue equivalence principles, and optimal bidding strategies for each auction format.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Auctioneer Section */}
            {activeSection === 'auctioneer' && (
                <div>
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h1 className="text-3xl font-bold text-orange-400 mb-4 flex items-center">
                            <UsersIcon className="h-6 w-6 mr-2"/> Auctioneer Dashboard
                        </h1>
                        <p className="text-gray-300 mb-4">
                            The Auctioneer Dashboard is designed for buyers conducting renewable energy procurement auctions. 
                            It helps optimize auction design, predict outcomes, and achieve the best value for your projects.
                        </p>
                    </div>

                    {/* Buyer Perspective Features */}
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h2 className="text-2xl font-bold text-blue-400 mb-4">Buyer Perspective Features</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                                    <DollarSignIcon className="h-5 w-5 mr-2"/> Project & Budget Management
                                </h3>
                                <ul className="text-gray-300 space-y-2">
                                    <li>• <strong>Budget Configuration:</strong> Set maximum budget, target budget, and reserve prices</li>
                                    <li>• <strong>Project Types:</strong> Configure requirements for different renewable energy projects</li>
                                    <li>• <strong>Cost Optimization:</strong> Predict budget savings and cost outcomes</li>
                                    <li>• <strong>Risk Management:</strong> Set reserve prices to avoid overpaying</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                                    <SettingsIcon className="h-5 w-5 mr-2"/> Bidder Pool Modeling
                                </h3>
                                <ul className="text-gray-300 space-y-2">
                                    <li>• <strong>Bidder Profiles:</strong> Model expected participant types and their characteristics</li>
                                    <li>• <strong>Cost Structure Analysis:</strong> Estimate bidder cost means and uncertainties</li>
                                    <li>• <strong>Quality Scoring:</strong> Predict technical and experience scores</li>
                                    <li>• <strong>Market Intelligence:</strong> Incorporate knowledge of competitor capabilities</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Auction Design Optimization */}
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h2 className="text-2xl font-bold text-green-400 mb-4">Auction Design Optimization</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gray-900/50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-purple-400 mb-2">Evaluation Criteria</h4>
                                <ul className="text-gray-300 text-sm space-y-1">
                                    <li>• <strong>Multi-Attribute Weighting:</strong> Balance price vs. quality factors</li>
                                    <li>• <strong>Technical Scoring:</strong> Weight for technical proposal quality</li>
                                    <li>• <strong>Experience Evaluation:</strong> Factor in bidder track record</li>
                                    <li>• <strong>Scoring Validation:</strong> Ensures weights sum to 100%</li>
                                </ul>
                            </div>
                            <div className="bg-gray-900/50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-yellow-400 mb-2">Format Selection</h4>
                                <ul className="text-gray-300 text-sm space-y-1">
                                    <li>• <strong>Revenue Equivalence:</strong> Compare expected outcomes across formats</li>
                                    <li>• <strong>Strategic Implications:</strong> Understand bidder behavior in each format</li>
                                    <li>• <strong>Transparency vs. Competition:</strong> Balance open vs. sealed bid approaches</li>
                                    <li>• <strong>Implementation Considerations:</strong> Practical aspects of each auction type</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Outcome Prediction */}
                    <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                        <h2 className="text-2xl font-bold text-cyan-400 mb-4">Outcome Prediction & Analysis</h2>
                        <div className="bg-gray-900/50 p-4 rounded-lg mb-4">
                            <h3 className="text-lg font-semibold text-white mb-2">Expected Results Dashboard</h3>
                            <p className="text-gray-300 text-sm mb-3">
                                Comprehensive analysis of expected auction outcomes to optimize your procurement strategy.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-500/30">
                                    <h4 className="text-blue-300 font-semibold">Winning Bid Prediction</h4>
                                    <p className="text-gray-300 text-xs">Expected final contract price with confidence intervals</p>
                                </div>
                                <div className="bg-green-900/30 p-3 rounded-lg border border-green-500/30">
                                    <h4 className="text-green-300 font-semibold">Budget Savings</h4>
                                    <p className="text-gray-300 text-xs">Savings vs. target budget with percentage calculations</p>
                                </div>
                                <div className="bg-purple-900/30 p-3 rounded-lg border border-purple-500/30">
                                    <h4 className="text-purple-300 font-semibold">Winner Quality</h4>
                                    <p className="text-gray-300 text-xs">Expected technical and experience scores of winner</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-orange-900/20 border border-orange-700/50 p-4 rounded-lg">
                            <h4 className="text-lg font-semibold text-orange-400 mb-2">Strategic Advantages</h4>
                            <p className="text-gray-300 text-sm">
                                <strong>Revenue Equivalence:</strong> All auction formats yield theoretically equivalent expected revenues under 
                                ideal conditions. <strong>Practical Differences:</strong> Implementation complexity, bidder preferences, 
                                and market dynamics may favor specific formats in real-world scenarios.
                            </p>
                        </div>
                    </div>

                    {/* Best Practices */}
                    <div className="bg-gray-800/50 rounded-lg p-6">
                        <h2 className="text-2xl font-bold text-red-400 mb-4">Auctioneer Best Practices</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gray-900/50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-green-400 mb-2">Maximize Participation</h4>
                                <ul className="text-gray-300 text-sm space-y-1">
                                    <li>• Clear, detailed project specifications</li>
                                    <li>• Reasonable bid preparation timeframes</li>
                                    <li>• Transparent evaluation criteria</li>
                                    <li>• Fair and consistent communication</li>
                                </ul>
                            </div>
                            <div className="bg-gray-900/50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-blue-400 mb-2">Quality Assurance</h4>
                                <ul className="text-gray-300 text-sm space-y-1">
                                    <li>• Multi-attribute evaluation systems</li>
                                    <li>• Technical capability assessments</li>
                                    <li>• Financial stability requirements</li>
                                    <li>• Reference and track record verification</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 